"use client";

import { useState } from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import CTA from "@/app/(components)/specifics/pages/Home/components/CTA/CTA";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { PlanSettings } from "@/app/types/billing/PlanSettings";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { HomeCtaAction } from "@/app/types/common/HomeCtaAction";
import { HomePricingPeriod } from "@/app/types/home/HomePricing";
import { UCreditOffer } from "@/app/utils/UCreditOffer";
import { UStyles } from "@/app/utils/UStyles";
import { CREDIT_TIERS, FounderOffer } from "@/app/vars/Credits";
import homeStyles from "../../Home.module.css";
import {
  buildProFeatureValues,
  fillPricingText,
  HOME_PRICING_CUSTOM_FEATURES,
  HOME_PRICING_PERIODS,
  HOME_PRICING_PRO_FEATURES,
} from "./Pricing.logic";
import styles from "./Pricing.module.css";
import PricingFeatures from "./PricingFeatures";
import PricingVolume from "./PricingVolume";

type Props = DefaultProps & {
  plan: PlanSettings;
};

export default function Pricing(props: Props) {
  const T = useT();
  const { params, plan } = props;
  const { lang } = params;

  const [period, setPeriod] = useState<HomePricingPeriod>(HomePricingPeriod.ANNUAL);
  const [tier, setTier] = useState(CREDIT_TIERS[0]);

  const isAnnual = period === HomePricingPeriod.ANNUAL;
  const discount = plan.annualDiscountPercent;
  const monthlyPrice = UCreditOffer.monthlyPriceEur(tier, isAnnual, discount);
  const publicPrice = UCreditOffer.publicPriceEur(monthlyPrice, FounderOffer.DISCOUNT_PERCENT);
  const largestTier = CREDIT_TIERS[CREDIT_TIERS.length - 1];

  const billingNote = isAnnual
    ? fillPricingText(T(TKey.HOME_MEIDLY_PRICING_BILLED_ANNUALLY), {
        price: UCreditOffer.annualPriceEur(tier, discount),
      })
    : T(TKey.HOME_MEIDLY_PRICING_BILLED_MONTHLY);

  return (
    <div className={styles.container}>
      <div className={homeStyles.titles}>
        <h3 className={homeStyles.supTitle}>{T(TKey.HOME_MEIDLY_PRICING_SUP_TITLE)}</h3>
        <h2 className={homeStyles.statement}>{T(TKey.HOME_MEIDLY_PRICING_TITLE)}</h2>
      </div>

      <div className={styles.periods}>
        {HOME_PRICING_PERIODS.map((tab) => (
          <button
            type="button"
            key={tab.period}
            onClick={() => setPeriod(tab.period)}
            className={UStyles.classNames(
              styles.period,
              tab.period === period ? styles.periodActive : "",
            )}
          >
            {T(tab.labelKey)}
            {tab.period === HomePricingPeriod.ANNUAL && (
              <span className={styles.periodSave}>
                {fillPricingText(T(TKey.HOME_MEIDLY_PRICING_PERIOD_SAVE), { discount })}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className={styles.plans}>
        <div className={UStyles.classNames(styles.plan, styles.planPro)}>
          {FounderOffer.ENABLED && (
            <span className={styles.founderBadge}>{T(TKey.HOME_MEIDLY_PRICING_FOUNDER_BADGE)}</span>
          )}
          <div className={styles.planHead}>
            <span className={styles.planName}>{T(TKey.HOME_MEIDLY_PRICING_PRO_NAME)}</span>
            <PricingVolume
              lang={lang}
              tiers={CREDIT_TIERS}
              selected={tier}
              isAnnual={isAnnual}
              discountPercent={discount}
              onSelect={setTier}
            />
          </div>

          <p className={styles.price}>
            <span className={styles.priceAmount}>{`${monthlyPrice}€`}</span>
            {FounderOffer.ENABLED && (
              <span className={styles.priceStruck}>
                <span className={styles.srOnly}>{T(TKey.HOME_MEIDLY_PRICING_PUBLIC_PRICE)}</span>
                {`${publicPrice}€`}
              </span>
            )}
            <span className={styles.priceUnit}>{T(TKey.HOME_MEIDLY_PRICING_PER_MONTH)}</span>
            <span className={styles.priceVat}>{T(TKey.HOME_MEIDLY_PRICING_VAT)}</span>
          </p>

          <p className={styles.billing}>{billingNote}</p>

          <p className={styles.tagline}>{T(TKey.HOME_MEIDLY_PRICING_PRO_TAGLINE)}</p>

          <div className={styles.cta}>
            <CTA
              params={params}
              min={false}
              hideIcon={true}
              action={HomeCtaAction.SIGN_UP}
              ctaKey={TKey.HOME_CTA_START_FREE}
            />
          </div>

          <p className={styles.included}>{T(TKey.HOME_MEIDLY_PRICING_INCLUDED)}</p>

          <PricingFeatures
            features={HOME_PRICING_PRO_FEATURES}
            values={buildProFeatureValues(lang)}
          />
        </div>

        <div className={styles.plan}>
          <div className={styles.planHead}>
            <span className={styles.planName}>{T(TKey.HOME_MEIDLY_PRICING_CUSTOM_NAME)}</span>
          </div>

          <p className={styles.price}>
            <span className={styles.priceTalk}>{T(TKey.HOME_MEIDLY_PRICING_CUSTOM_PRICE)}</span>
          </p>

          <p className={styles.billing}>
            {fillPricingText(T(TKey.HOME_MEIDLY_PRICING_CUSTOM_BILLING), {
              credits: UCreditOffer.formatCredits(largestTier.credits, lang),
            })}
          </p>

          <p className={styles.tagline}>{T(TKey.HOME_MEIDLY_PRICING_CUSTOM_TAGLINE)}</p>

          <div className={styles.cta}>
            <Button
              params={params}
              isLink={false}
              type={ButtonType.OUTLINE}
              text={T(TKey.HOME_CTA_BOOK_DEMO)}
              onClick={() => ModalStore.getState().openDemo()}
            />
          </div>

          <p className={styles.included}>{T(TKey.HOME_MEIDLY_PRICING_INCLUDED)}</p>

          <PricingFeatures features={HOME_PRICING_CUSTOM_FEATURES} />
        </div>
      </div>
    </div>
  );
}
